import {
  GridApi,
  IServerSideGetRowsRequest,
  RowEvent,
  RowNode,
} from 'ag-grid-community';
import { CancelToken } from 'axios';
import { api, rtr } from '.';
import { getCSRFToken } from './csrf';
import { getTimezoneOffset } from '../utils/dateUtils';

export interface BIN {
  active: boolean;
  bin: number | string;
  pruned_bin: string;
  bin_length: number;
  bin_start_position: number;
  fee_tier: string;
  fraud_ind: string;
  ic_code: string;
  intg_fee: string;
  min_pan_length: number;
  net_id: string;
  atm_ind: string;
  pinless_pos_ind: string;
  credit_ind: string;
  debit_ind: string;
  network_identifier: number;
  pan_legnth: number;
  pos_ind: string;
  source_network: string;
  start_dttm: string;
  end_dttm: string;
  overriden?: string[];
}

export interface deltaBIN {
  action_type: string;
  bin_source_id: number;
  bin_source_name: string;
  create_date_time: string;
  delta_hk: string;
  update_date_time: string;
  bin: number;
  pruned_bin: string;
  bin_length: number;
  bin_start_position: number;
  fee_tier: string;
  fraud_ind: string;
  intg_fee: string;
  min_pan_length: number;
  net_id: string;
  pan_length: number;
  pos_ind: string;
  atm_ind: string;
  pinless_pos_ind: string;
  credit_ind: string;
  debit_ind: string;
  network_identifier: number;
  source_network: string;
  network_common_name: string;
  routable_network: string;
  start_dttm: string;
  ic_code: string;
  action: string;
}

export interface BINOverride {
  name: string;
  new_value: string;
  bin_value: string;
  master_hk: string;
  bin_source_name: string;
  create_date_time: string;
  update_date_time: string;
  end_date_time: string | undefined;
  override_type: 'sticky' | 'nonsticky';
  override_id: string;
  approval_status: string;
  override_description: string;
  author: string;
  approver?: string;
  raw_value: string;
  net_id: string;
  routing_network: string;
}

export interface BINSchema {
  field: string;
  display_name: string;
  default_hidden: boolean;
  editable: boolean;
  valueMapping: string;
  component_type: 'dropdown' | 'textbox';
  pinned?: 'left' | 'right' | undefined;
  type: 'bool' | 'int' | 'str' | 'date' | 'datetime';
}

export interface BINInfo {
  bin: BIN;
  override: {
    non_sticky_list: string[];
    sticky_list: string[];
  };
}

export interface BINSearchResponse {
  schema: BINSchema[];
  bin_info: BINInfo[];
}

export interface paginatedBINResponse {
  totalRecords: number;
  data: BINInfo[];
}

export interface BINCountsResponse {
  active_count: number;
  nonsticky: number;
  sticky: number;
  total_records: number;
}

export interface ApprovalMetaInfo {
  active: number;
  pending: number;
  rejected: number;
  approved: number;
  expired: number;
  ['pending removal']: number;
  ['approved removal']: number;
  ['rejected removal']: number;
  ['etl batch expiry']: number;
  ['prior etl batch expired']: number;
}

export interface ActiveApprovalMetaInfo {
  effective: number;
  future: number;
}

export interface DateRangeObject {
  from: Date;
  to: Date;
}

export interface InitialBINState {
  binCounts: BINCountsResponse;
  binFilters: BINFilter[];
  binRecords: BINInfo[];
  filterModel: IServerSideGetRowsRequest['filterModel'];
  filterOptions: {
    [key: string]: any;
  };
  loadedBINType?: string | undefined;
  loadDate?: Date | null | undefined;
  pageNo: number;
  quickFilters: BINQuickFilters;
}

export interface BINQuickFilters {
  active: 'none' | 'Y' | 'N';
  nonsticky: boolean;
  sticky: boolean;
}

export interface BINFilter {
  field: string;
  condition:
    | '=='
    | '!='
    | '>='
    | '<='
    | 'in'
    | 'inrange'
    | 'like'
    | 'between'
    | 'ornull'
    | '';
  value: string | string[] | Date | DateRangeObject | NumberRangeObject[];
  isQuickFilter?: boolean;
}

export interface sortSet {
  field: string;
  sort: 'asc' | 'desc';
}

export interface NumberRangeObject {
  fromfield: string;
  fromvalue: Number;
  tofield: string;
  tovalue: Number;
}

export interface paginatedBINFilter {
  pageSize: number;
  pageNo: number;
  filters: BINFilter[];
  sortBy?: sortSet;
  quickFilters?: BINFilter[];
  loaded_bin_type?: string;
}

export interface BINCounts {
  filters: BINFilter[];
  quickFilters?: BINFilter[];
  loaded_bin_type?: string;
}

export interface AllFilters {
  binFilters: any;
  quickFilters: any;
  sortModel: any;
  loadedBIN: string;
}

export interface PaginatedMyOverrides {
  pageSize: number;
  pageNo: number;
  filters: BINFilter[];
  sortBy?: sortSet;
  active?: boolean;
  activeTab?: string;
  user?: string;
}

export interface CellOverrides {
  sticky: boolean;
  nonsticky: boolean;
}

export interface OverridesData {
  sticky: number;
  nonsticky: number;
}

export interface OverridesCount {
  sticky: number;
  nonsticky: number;
}

export interface exportBINReqParams {
  filters: BINFilter[];
  file_download_type: '.xlsx' | '.pdf' | '.csv';
  file_name: string;
}

export interface Networks {
  network: string;
  spec_owner: string;
  code: string;
}

export interface ExpiritationCount {
  critical: number;
  urgent: number;
  important: number;
  expired: number;
}

export interface BinOverridesCount {
  all_overrides: number;
  my_overrides: number;
  my_approvals: number;
}

export interface NetworkMaintenanceRequestsCount {
  pending_request: number;
  approved_request: number;
  rejected_request: number;
}

export interface AllNetworks {
  id: number;
  label: string;
  value: string;
}
export interface Transaction {
  mcc: number | '';
  transaction_amount: number | '';
  merchant_indicator: string | '';
  bin_number: number | '';
  version: number | '';
  uuid: number | '';
}

export interface SOTT {
  code: number;
  sott_fee: number;
  description: string;
  category: string;
}

export interface NetworkFee {
  matchedBin: number;
  fee_by_sott: SOTT[];
  fee_total: number;
  ic_code: string;
  merchant_override_status: number;

  merchant_tier: string;
  routable_network: string;
  routing_order_rank: number;
}

export interface SimulationResponse {
  artifact_file_id: string;
  card_bin_number: number;
  mcc: number;
  merchant_indicator: number;
  network_fees: NetworkFee[];
  transaction_amount: number;
  uid?: string;
}

export interface BINFileSummary {
  additions: number;
  create_date_time: string;
  bin_source_name: string;
  bins_updates: number;
  fields_updated: number;
  deletions: number;
}

export interface DeltaBINResponse {
  deltabin_info: deltaBIN[];
  schema: BINSchema[];
}

export interface BINSummaryCount {
  ADD: number;
  DELETE: number;
  UPDATE: number;
}

export const getAuthRoles = async (key: string, accessToken: string) => {
  await getCSRFToken();
  const response = await api.post<any>('/auth', null, {
    headers: { bearer: `${accessToken}` },
  });
  return response?.data;
};

export const doLogout = async (key: string) => {
  const response = await api.post<any>('/logout');
  return response?.data;
};

export const getUserGroups = async (key: string) => {
  const response = await api.get<any>('/userroles');
  return response?.data;
};

export const fetchFieldOptions = async (
  field: string,
  userId: string,
) => {
  const response = await api.get('bin/fieldOptions', {
    params: { column: field },
  });
  return response?.data;
};

export const fetchSchema = async (
  type: string,
  userId: string,
  featureName?: string,
) => {
  const response = await api.get('/schema', {
    params: { resourceType: type, userid: userId, feature_name: featureName },
  });
  return response?.data;
};

export const fetchBINs = async (key: string, filters: BINFilter[]) => {
  const response = await api.post<BINSearchResponse>('/bin', filters);
  return response?.data;
};

export const fetchBinDetails = async (request: any) => {
  const response = await api.get(`/bin/details/${request?.master_hk}`, {
    params: {
      create_date_time: request?.createDate,
      active_flag: request?.activeFlg,
    },
  });
  return response?.data;
};

export const getPaginatedBINs = async (
  requestPayload: paginatedBINFilter,
  cancelToken?: CancelToken,
) => {
  const response = await api.post<paginatedBINResponse>(
    '/bin/paginated',
    requestPayload,
    {
      cancelToken,
    },
  );
  return response?.data;
};

export const getBINCounts = async (
  requestPayload: BINCounts,
  cancelToken?: CancelToken,
) => {
  const response = await api.post<BINCountsResponse>(
    '/bin/count',
    requestPayload,
    {
      cancelToken,
    },
  );
  return response?.data;
};

export const getPaginatedMyOverrides = async (
  requestPayload: PaginatedMyOverrides,
  cancelToken?: CancelToken,
) => {
  const response = await api.post<{
    approval_meta: any;
    data: any[];
    total_records: number;
  }>('/bin/override', requestPayload, {
    cancelToken,
  });
  return response?.data;
};

export const exportBINRecords = async (requestPayload: exportBINReqParams) => {
  const response = await api.post('/download', requestPayload, {
    responseType: 'blob',
  });
  return response;
};

export const saveOverriddenBINData = async (
  overriddenBINData: BINOverride[],
) => {
  const response = await api.post('/bin/override/addOrUpdate', {
    payload: overriddenBINData,
  });
  return response?.data;
};

export const editOverriddenBINData = async (
  overriddenBINData: BINOverride[],
) => {
  const response = await api.patch('/bin/override/edit', {
    payload: overriddenBINData,
  });
  return response?.data;
};

export const fetchBINOverrideDetails = async (
  overrideId: string,
) => {
  const response = await api.get(`/bin/override/${overrideId}`);
  return response?.data;
};

export const approveBINOverride = async (override: {}) => {
  const response = await api.patch('/bin/override/approve', override);
  return response?.data;
};

export const rejectBINOverride = async (override: {}) => {
  const response = await api.patch('/bin/override/reject', override);
  return response?.data;
};

export const deleteBINOverride = async (override: {}) => {
  const response = await api.patch(`/bin/override/delete`, override);
  return response?.data;
};

export const deactivateBINOverride = async (override: {}) => {
  const response = await api.patch(`/bin/override/deactivate`, override);
  return response?.data;
};

export const removeGridSettings = async (reqPayload: {}) => {
  await api.delete('/bin/gridconfig', { data: reqPayload });
};

export const saveGridConfig = async (requestPayload: {}) => {
  const response = await api.post('/bin/gridconfig', requestPayload);
  return response?.data;
};

export const updateGridConfig = async (requestPayload: {}) => {
  const response = await api.put('/bin/gridconfig', requestPayload);
  return response?.data;
};

export const fetchBINFile = async (payload: {}) => {
  const response = await api.post<BINFileSummary[]>(
    '/binfile/summary',
    payload,
  );
  return response?.data;
};

export const fetchLatestBINLoads = async (payload: {}) => {
  const response = await api.post<BINFileSummary[]>(
    '/binfile/summary/latest',
    payload,
  );
  return response?.data;
};

export const fetchBINSummaryCount = async (key: string) => {
  const response = await api.get<BINSummaryCount>('/binfile/count');
  return response?.data;
};

export const fetchDeltaBINs = async (key: string, filters: BINFilter[]) => {
  const data = { filters: filters };
  const response = await api.post<DeltaBINResponse>('/binfile/deltabin', data);
  return response?.data;
};

export const fetchBinConfig = async () => {
  const response = await api.get('/binconfig');
  return response?.data;
};

export const fetchNetworks = async () => {
  const response = await api.get<Networks[]>('/network');
  return response?.data;
};

export const fetchColumnPickList = async (requestPayload: any) => {
  const response = await api.post<any>('/bin/fieldOptions', requestPayload);
  return response?.data;
};

export const fetchOverridesColumnPickList = async (requestPayload: any) => {
  const response = await api.post<any>(
    '/bin/override/fieldoptions',
    requestPayload,
  );
  return response?.data;
};

export const fetchAllNetworks = async () => {
  const response = await api.get<any>('/binconfig/network');
  return response?.data;
};

export const fetchBinSearchOptions = async () => {
  const response = await api.get<any>('/bin/searchoptions');
  return response?.data;
};

export const fetchExpirationcount = async () => {
  const response = await api.get<ExpiritationCount>(
    '/bin/override/expirationcount',
    {
      params: {
        offset: getTimezoneOffset(),
      },
    },
  );
  return response?.data;
};

export const fetchBinOverridesCount = async (key: string) => {
  const response = await api.get<BinOverridesCount>('/bin/override/count');
  return response?.data;
};

export const fetchNetworkMaintenanceOverrideRequestCount = async (key: string) => {
  const response = await api.get<NetworkMaintenanceRequestsCount>(
    '/maintenanceoverrides/requestscount',
  );
  return response?.data;
};

export const fetchConfigMappings = async (params: any) => {
  const response = await api.post<ConfigMappings>('/binconfig/mapping', params);
  return response?.data;
};

export interface MappingObj {
  sources: Sources[];
  mapping: any;
}

export interface ConfigMappings {
  normalizedField: string;
  normalizedFieldDisplayName: string;
  mappings: MappingObj[];
  defaultsTo: string;
}

export interface Sources {
  field: string;
  displayName: string;
  start: number;
  length: number;
}

export interface cellOverrideObject {
  value: string;
  description: string;
  status: string;
  expiry_date: string;
  old_value: string;
}

export interface UnsavedOverriddenBinData {
  column: string;
  oldValue: string;
  newValueObj: cellOverrideObject;
  rowNode: RowNode;
  data: RowEvent['data'];
  api: GridApi;
}

export const simulateTransaction = async (
  key: string,
  transaction: Transaction | null,
) => {
  if (transaction) {
    const response = await rtr.post<SimulationResponse>(
      '/simulate',
      transaction,
    );
    return response;
  }
};
